import { Component, computed, effect, inject, signal } from '@angular/core';
import {
	BasicFilterExpression,
	DataOptions,
	DataResponse,
	DataService,
	DoorstroomMeasure,
	ExportDataOptions,
	ExportFilter,
} from '../../services/data.service';
import { Observable } from 'rxjs';
import { Level, Path } from '../../services/data-tree';
import { ColumnDef, TableModel } from '../../shared/components/table/table/table.model';
import { att, percOfRow } from '../../services/measures';
import { nth, range, sum } from 'lodash-es';
import { maxOver, noAgg0, sumIf, sumOver } from '../../services/aggregation';
import { SingleSelectDropdownComponent } from '@cumlaude/shared-components-inputs';
import { Option } from '@cumlaude/shared-utils';
import { getNiveauVerschil, onderwijspositie_0_5_10 } from '../../services/onderwijspositie';
import { BarInfo } from '../../services/stacked-bars';
import { createMeasureColumn, DataRow } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { Router } from '@angular/router';
import { formatPercent } from '@angular/common';
import { BarchartTableConfig } from '../../shared/dashboard/barchart-table/barchart-table-config';
import { Attributes, SelectionConfig } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DashboardContext, DataRequestContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { AttrPath, Table } from '@cumlaude/metadata';
import { DashboardVariant, THRESHOLD_VALUES } from '../../services/weergave-opties';
import { PartitionMeasure, VbarchartTableComponent } from '../../shared/dashboard/vbarchart-table/vbarchart-table.component';
import { TooltipElement } from '@cumlaude/shared-components-overlays';
import { PsName } from '../../services/page-state.service';
import { BarchartTableComponent } from '../../shared/dashboard/barchart-table/barchart-table.component';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { FilterName } from '../../services/filter-name';
import { LegendaComponent } from '../../legenda/legenda.component';
import { ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';

interface PlaatsingAdviesI extends Attributes {
	ds_fun_co_verschil_basisschooladvies_van: string | null;
	'ds_fk_ll.ll_nm_basisschooladvies': string | null;
	ds_m_nr_weging: number;
	ds_m_is_prognose: number;
	ds_m_nr_leerlingen: number;
}

interface PlaatsingAdviesA extends Attributes {
	verschil_weging: number;
	onbekend: number;
	onbekend_lln: number;
	bekend: number;
	bekend_lln: number;
	weging: number;
	ds_m_is_prognose: number;
	ds_m_nr_leerlingen: number;
}

@Component({
	selector: 'app-plaatsing-advies',
	templateUrl: './plaatsing-advies.component.html',
	styleUrls: ['./plaatsing-advies.component.scss'],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		BarchartTableComponent,
		VbarchartTableComponent,
		FilterBarComponent,
		SingleSelectDropdownComponent,
		LegendaComponent,
		ToggleButtonsComponent,
	],
})
export class PlaatsingAdviesComponent extends BarchartTableConfig<PlaatsingAdviesI, PlaatsingAdviesA> {
	private readonly dataService = inject(DataService);
	protected readonly router = inject(Router);

	/**
	 * Bij wijziging van het aantal subgroups ook DoorstroomEndpoint.NR_SUBGROUPS_ADVIES_PLAATSING_DASHBOARD aanpassen.
	 */
	readonly subgroups = signal<AttrPath[]>([['ds_fun_co_verschil_basisschooladvies_van'], ['ds_fk_ll', 'll_nm_basisschooladvies']]).asReadonly();

	readonly historieGroups = computed(() => this.groeperingService.huidigeGroeperingen().slice(0, -1));

	readonly historieSubgroups = computed(() => [
		...this.groeperingService.huidigeGroeperingen().slice(-1),
		['ds_nm_schooljaar_van'] as AttrPath,
		...this.subgroups(),
	]);

	override availableGroups: AttrPath[] = [
		['ds_co_brin_svh'],
		['ds_nm_klas_van'],
		['ds_nr_leerjaar_van'],
		['ds_fk_ilt_van', 'ilt_nm_niveau'],
		['ds_nm_opleiding_van'],
		['ds_fk_ilt_van', 'ilt_nm_opleiding'],
		['ds_fk_ilt_van', 'ilt_abb_profiel'],
		['ds_fk_ll', 'll_nm_svh'],
		['ds_fk_vs_van', 'vs_nm_vestiging'],
	];

	actueelFilters: FilterName[] = [
		'ds_nm_schooljaar_van',
		'ds_fk_lb_van.lb_co_brin',
		'x_peildatum',
		'x_advies_leerlinggroep',
		'ds_fk_vs_van.vs_nm_vestiging',
		'ds_fk_ilt_van.ilt_nm_niveau',
		'ds_nr_leerjaar_van',
		'ds_fk_ll.ll_nm_svh',
		'ds_co_brin_svh',
	];

	historieFilters: FilterName[] = [
		'x_doorstroom_schooljaar_historie', //
		'x_doorstroom_multiselect_schooljaar',
		...this.actueelFilters,
	];

	permanentFilterExpressions = [
		// Voorkom dubbelingen door doorstroom-record van relevante plaatsing naar zowel niet- als wel-relevante plaatsing
		new BasicFilterExpression(['ds_is_plaatsing_opeenvolgend'], 1),
		new BasicFilterExpression(['ds_nr_weging'], 0, '>'),
	];

	thresholdOpties = THRESHOLD_VALUES.map((aantal) => new Option<number>(aantal, aantal === 0 ? 'Alle' : `≥ ${aantal}`));

	variant = this.qp.signal('variant');

	threshold = this.qp.signal('threshold');

	constructor() {
		super();
		effect(() => {
			this.threshold();
			this.filterService.refresh();
		});
	}

	getSelectedThresholdOptie() {
		return this.thresholdOpties.find((option) => option.value === this.threshold())!;
	}

	factTable = Table.fac_ds_doorstroom;

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.dataService.getDoorstroomData({
			...options,
			threshold: this.threshold(),
			m: [DoorstroomMeasure.LEERLINGEN],
			r: range(0, this.getRowLevel() + 1),
		});
	}

	override getExportData(options: ExportDataOptions) {
		return this.dataService.getDoorstroomExportData(options);
	}

	override getDisplayOptions(): ExportFilter[] {
		return [{ label: 'Aantal leerlingen', value: this.getSelectedThresholdOptie().text }];
	}

	protected override singleAggregators = {
		verschil_weging: {
			init: ({ ds_fun_co_verschil_basisschooladvies_van, ds_m_nr_weging }: PlaatsingAdviesI) =>
				(onderwijspositie_0_5_10(ds_fun_co_verschil_basisschooladvies_van) / 10) * ds_m_nr_weging,
			combine: (as: number[]) => sum(as),
		},
		bekend: sumIf<'ds_m_nr_weging', PlaatsingAdviesI>(
			'ds_m_nr_weging',
			({ ds_fun_co_verschil_basisschooladvies_van }: PlaatsingAdviesI) => ds_fun_co_verschil_basisschooladvies_van !== null
		),
		bekend_lln: sumIf<'count_records', PlaatsingAdviesI>(
			'count_records',
			({ ds_fun_co_verschil_basisschooladvies_van }: PlaatsingAdviesI) => ds_fun_co_verschil_basisschooladvies_van !== null
		),
		onbekend: sumIf<'ds_m_nr_weging', PlaatsingAdviesI>(
			'ds_m_nr_weging',
			({ ds_fun_co_verschil_basisschooladvies_van }: PlaatsingAdviesI) => ds_fun_co_verschil_basisschooladvies_van === null
		),
		onbekend_lln: sumIf<'count_records', PlaatsingAdviesI>(
			'count_records',
			({ ds_fun_co_verschil_basisschooladvies_van }: PlaatsingAdviesI) => ds_fun_co_verschil_basisschooladvies_van === null
		),
		weging: sumOver<'ds_m_nr_weging', PlaatsingAdviesI, number>('ds_m_nr_weging'),
		ds_m_is_prognose: maxOver<'ds_m_is_prognose', PlaatsingAdviesI>('ds_m_is_prognose'),
		ds_m_nr_leerlingen: noAgg0<'ds_m_nr_leerlingen', PlaatsingAdviesI>('ds_m_nr_leerlingen'),
	};

	override makeBar(
		attrs: PlaatsingAdviesI,
		path: Path<PlaatsingAdviesA, number[]>,
		context: DashboardContext<PlaatsingAdviesI, PlaatsingAdviesA, PlaatsingAdviesComponent>
	): BarInfo {
		const { 'ds_fk_ll.ll_nm_basisschooladvies': ll_nm_basisschooladvies, ds_fun_co_verschil_basisschooladvies_van, ds_m_nr_weging } = attrs;
		const verschil_niv = ds_fun_co_verschil_basisschooladvies_van;
		const niveauVerschilLabel = getNiveauVerschil(verschil_niv!);
		const basisschooladvies = ll_nm_basisschooladvies;
		const kleur = verschil_niv === '+5' ? '5hv' : verschil_niv;

		const totalRowN = this.subgroups().length + 1;
		const totalRow = nth(path, -totalRowN)?.a.weging;
		const percentage = formatPercent(ds_m_nr_weging / totalRow!, 'nl-NL', '1.0-0');

		const tooltipElements: TooltipElement[] = [
			{ label: `Niveauverschil:`, value: `${Number(verschil_niv) / 10}` },
			{ label: 'Advies:', value: `${basisschooladvies}` },
			{ label: `Aantal:`, value: `${ds_m_nr_weging} van ${totalRow}` },
			{ label: `Percentage:`, value: `${percentage}` },
		];

		return {
			...super.makeBar(attrs, path, context),
			className: `verschil-${kleur}`,
			tooltip: tooltipElements,
			description: `${niveauVerschilLabel}`,
		};
	}

	override partitionBarData(rowRoot: Level<PlaatsingAdviesA, number[]>): Path<PlaatsingAdviesA, number[]>[][] {
		return [rowRoot.r.filter((path) => nth(path, -2)!.k !== null)];
	}

	override createMeasureColumns(
		context: DashboardContext<PlaatsingAdviesI, PlaatsingAdviesA, PlaatsingAdviesComponent>
	): ColumnDef<DataRow<PlaatsingAdviesA>>[] {
		if (this.variant() === DashboardVariant.HISTORIE) return [];

		return [
			createMeasureColumn('Advies verschil', percOfRow('verschil_weging', 'bekend'), { dataType: 'percentage', format: '+1.0-0' }),
			this.createLeerlingColumn('Leerlingen', att('bekend'), { context }, att('bekend_lln'), [
				new BasicFilterExpression(['ds_fun_co_verschil_basisschooladvies_van'], null, '<>'),
			]),
			this.createLeerlingColumn('Verschil onbekend', att('onbekend'), { context }, att('onbekend_lln'), [
				new BasicFilterExpression(['ds_fun_co_verschil_basisschooladvies_van'], null),
			]),
		];
	}

	override isHistorieBatchVariant(): boolean {
		return this.variant() === DashboardVariant.HISTORIE && this.groeperingService.huidigeAantalGroeperingen() > 0;
	}

	getRowLevel() {
		return this.variant() === DashboardVariant.ACTUEEL ? this.groeperingService.huidigeAantalGroeperingen() : this.historieGroups().length;
	}

	override enrichTableModel(
		_context: DashboardContext<PlaatsingAdviesI, PlaatsingAdviesA, PlaatsingAdviesComponent>,
		tableModel: TableModel<DataRow<PlaatsingAdviesA>>
	) {
		tableModel.showFooters = this.variant() === DashboardVariant.ACTUEEL;
	}

	override onContextCreated(context: DashboardContext<PlaatsingAdviesI, PlaatsingAdviesA, PlaatsingAdviesComponent>) {
		this.pageStateService.dispatch(PsName.prognose, String(Boolean(context.dataRoot?.a.ds_m_is_prognose)));
	}

	override getSelectionConfig(context: DataRequestContext<PlaatsingAdviesA>): SelectionConfig<PlaatsingAdviesA> | undefined {
		return {
			...super.getSelectionConfig(context)!,
			getSize: att('ds_m_nr_leerlingen'),
			extraFilters: [],
		};
	}

	partitionMeasure: PartitionMeasure<PlaatsingAdviesA> = {
		type: 'percentage',
		getValue: percOfRow<keyof PlaatsingAdviesA, PlaatsingAdviesA>('verschil_weging', 'bekend'),
		format: '+1.0-0',
	};

	protected readonly DashboardVariant = DashboardVariant;
}
